import PriceDisplay from '@/components/PriceDisplay';
import PromoCode from '@/components/PromoCode';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import { Accordion, Icons, Typography } from '@fleet/webshop-ui';
import type { FC, ReactNode } from 'react';

interface AdmissionItemProps {
  icon: keyof typeof Icons;
  description?: ReactNode;
  amount: number;
  status: AdmissionDTO['status'];
  promoCode?: { code?: string; description?: string };
}

const AdmissionItem: FC<AdmissionItemProps> = ({
  icon,
  description,
  amount,
  status,
  promoCode,
}) => {
  const IconComponent = Icons[icon];
  const isRefunded = status === 'REFUNDED';
  const isReleased = status === 'RELEASED';
  const isExchanged = status === 'EXCHANGED';
  const isGrayedOut = isRefunded || isReleased || isExchanged;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start justify-between gap-2">
        <div className="flex gap-2">
          <IconComponent
            className={cn('h-4 w-4 text-primary', {
              'text-neutral': isGrayedOut,
            })}
          />
          <Typography
            variant="body2-bold"
            className={cn({
              'text-neutral': isGrayedOut,
            })}
          >
            {description}
          </Typography>
        </div>
        <Typography
          variant="body2-bold"
          className={cn('text-primary', {
            'text-neutral': isGrayedOut,
          })}
        >
          <PriceDisplay amount={amount} />
        </Typography>
      </div>
      {promoCode && (
        <Accordion
          className="ml-6 border-none"
          classNames={{ trigger: 'p-0 hover:no-underline' }}
          trigger={
            <Typography variant="body2-bold">
              <span className="text-success-text">
                <TransText i18nKey="promoCode" />
                {': '}
              </span>
              <Typography variant="body2" className="text-primary" as="span">
                {promoCode.code}
              </Typography>
            </Typography>
          }
        >
          <PromoCode {...promoCode} />
        </Accordion>
      )}
      {isRefunded && (
        <Typography
          variant="label"
          className="ml-6 w-fit rounded-sm bg-neutral px-1 py-0.5 text-white"
        >
          <TransText i18nKey="refunded" />
        </Typography>
      )}
      {isReleased && (
        <Typography
          variant="label"
          className="ml-6 w-fit rounded-sm bg-neutral px-1 py-0.5 text-white"
        >
          <TransText i18nKey="cancelled" />
        </Typography>
      )}
      {isExchanged && (
        <Typography
          variant="label"
          className="ml-6 w-fit rounded-sm bg-neutral px-1 py-0.5 text-white"
        >
          <TransText i18nKey="exchanged" />
        </Typography>
      )}
    </div>
  );
};

export default AdmissionItem;
